import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Download } from '@mui/icons-material';

import {
  buildCsv,
  downloadBlobFile,
  downloadTextFile,
  getDefaultReportPeriod,
  getReportErrorMessage,
  ReportEmptyState,
  ReportErrorState,
  ReportPeriod,
  ReportPeriodFilter,
  ReportsNavigation,
  toApiPeriodParams,
} from '../../components/reports/ReportFilters';
import reportsService, { SystemActivityReport } from '../../services/reportsService';

const SystemActivityPage: React.FC = () => {
  const [rows, setRows] = useState<SystemActivityReport[]>([]);
  const [period, setPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [appliedPeriod, setAppliedPeriod] = useState<ReportPeriod>(() => getDefaultReportPeriod());
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const response = await reportsService.getSystemActivity(toApiPeriodParams(appliedPeriod));
      setRows(response.data);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [appliedPeriod]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const csv = buildCsv(rows, [
      { header: 'date', value: (row) => row.date },
      { header: 'total_events', value: (row) => row.total_events },
      { header: 'user_actions', value: (row) => row.user_actions },
      { header: 'device_events', value: (row) => row.device_events },
      { header: 'playback_events', value: (row) => row.playback_events },
      { header: 'errors', value: (row) => row.errors },
      { header: 'active_devices', value: (row) => row.active_devices },
      { header: 'content_uploads', value: (row) => row.content_uploads },
    ]);
    downloadTextFile(csv, `system-activity-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.csv`);
  };

  const exportExcel = async () => {
    try {
      setExporting(true);
      const file = await reportsService.exportReport('system-activity', toApiPeriodParams(appliedPeriod), 'xlsx');
      downloadBlobFile(file, `system-activity-${appliedPeriod.dateFrom}-${appliedPeriod.dateTo}.xlsx`);
      setError(null);
    } catch (err) {
      setError(getReportErrorMessage(err, 'Не удалось экспортировать системную активность в Excel'));
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Системная активность</Typography>
          <Typography variant="body2" color="text.secondary">
            События, ошибки, загрузки и активные устройства по дням.
          </Typography>
        </Box>

        <ReportsNavigation />

        <ReportPeriodFilter
          period={period}
          onPeriodChange={setPeriod}
          onApply={() => setAppliedPeriod(period)}
          loading={loading}
          extraActions={(
            <Stack direction="row" spacing={1}>
              <Button variant="outlined" startIcon={<Download />} onClick={exportExcel} disabled={loading || exporting || rows.length === 0}>
                Excel
              </Button>
              <Button variant="outlined" startIcon={<Download />} onClick={exportCsv} disabled={loading || rows.length === 0}>
                CSV
              </Button>
            </Stack>
          )}
        />

        {error && <ReportErrorState title="Отчет не загрузился" message={error} onRetry={load} />}

        <Paper>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : rows.length === 0 ? (
            <ReportEmptyState title="Нет системных событий" message="За выбранный период системная активность не найдена." />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Дата</TableCell>
                  <TableCell>События</TableCell>
                  <TableCell>Пользователи</TableCell>
                  <TableCell>Устройства</TableCell>
                  <TableCell>Воспроизведения</TableCell>
                  <TableCell>Ошибки</TableCell>
                  <TableCell>Активные устройства</TableCell>
                  <TableCell>Загрузки</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.date}>
                    <TableCell>{new Date(row.date).toLocaleDateString()}</TableCell>
                    <TableCell>{row.total_events}</TableCell>
                    <TableCell>{row.user_actions}</TableCell>
                    <TableCell>{row.device_events}</TableCell>
                    <TableCell>{row.playback_events}</TableCell>
                    <TableCell>{row.errors}</TableCell>
                    <TableCell>{row.active_devices}</TableCell>
                    <TableCell>{row.content_uploads}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>
    </Box>
  );
};

export default SystemActivityPage;
